import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { Heart, Check, ArrowRight } from 'lucide-react';

const PROBLEMS = [
  { icon: '😤', title: '¿Quién le debe a quién?', desc: 'Siempre hay uno que paga más y nunca se cuadran las cuentas.' },
  { icon: '💸', title: 'Peleas por los gastos', desc: 'Quién gastó de más, quién no aporta suficiente. Un ciclo sin fin.' },
  { icon: '🙈', title: 'Cero transparencia', desc: 'Cada uno maneja su plata y nadie sabe el panorama completo.' },
  { icon: '😞', title: 'Metas que no avanzan', desc: 'Quieren ahorrar juntos pero no hay sistema para hacerlo.' },
];

const SOLUTIONS = [
  {
    badge: 'Balance compartido',
    title: 'Vean todo su dinero en un solo lugar',
    desc: 'Cada uno registra sus ingresos y gastos. Kuadra2 les muestra el panorama completo: cuánto aporta cada uno, cuánto se gasta entre los dos, y cuál es el balance real de la pareja.',
    features: ['Balance individual + compartido', 'Gastos categorizados', 'Ingresos de ambos visibles'],
  },
  {
    badge: 'Splits justos',
    title: 'Dividan gastos sin calculadora',
    desc: '¿Cena a medias? ¿Supermercado 60/40? Registren cada gasto compartido y Kuadra2 calcula automáticamente cuánto debe cada uno. Sin discusiones, sin olvidos.',
    features: ['50/50, 60/40, o custom', 'Tracking de quién pagó qué', 'Alertas de balance pendiente'],
  },
  {
    badge: 'Metas en pareja',
    title: 'Ahorren juntos con un objetivo claro',
    desc: 'Definan metas compartidas — vacaciones, casa, fondo de emergencia. Vean cuánto aporta cada uno y celebren juntos cuando lo logren.',
    features: ['Metas compartidas', 'Progreso individual visible', 'Celebración al completar'],
  },
];

function MockupSharedBalance() {
  const [view, setView] = useState('shared');

  return (
    <div className="rounded-2xl bg-white dark:bg-gray-800 shadow-lg border border-gray-100 dark:border-gray-700 p-5 w-full max-w-xs select-none">
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5 mb-3">
        {['shared', 'individual'].map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`flex-1 text-[9px] font-semibold py-1 rounded-md transition-all ${
              view === v
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-400'
            }`}
          >
            {v === 'shared' ? 'Compartido' : 'Individual'}
          </button>
        ))}
      </div>

      {view === 'shared' ? (
        <>
          <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Balance compartido</div>
          <div className="text-2xl font-extrabold text-gray-900 dark:text-white mb-4">$4,850,000</div>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-[9px] font-bold text-white">FR</div>
                <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300">Francisco</span>
              </div>
              <div className="text-sm font-bold text-gray-900 dark:text-white">$3,500,000</div>
            </div>
            <div className="w-px h-10 bg-gray-200 dark:bg-gray-700" />
            <div className="flex-1 text-right">
              <div className="flex items-center gap-1.5 mb-1 justify-end">
                <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300">Ana</span>
                <div className="w-6 h-6 rounded-full bg-pink-500 flex items-center justify-center text-[9px] font-bold text-white">AN</div>
              </div>
              <div className="text-sm font-bold text-gray-900 dark:text-white">$1,350,000</div>
            </div>
          </div>
          <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex">
            <div className="h-full bg-blue-500 rounded-l-full transition-all duration-500" style={{ width: '72%' }} />
            <div className="h-full bg-pink-500 rounded-r-full transition-all duration-500" style={{ width: '28%' }} />
          </div>
        </>
      ) : (
        <div className="space-y-3">
          {[
            { name: 'Francisco', initials: 'FR', color: 'bg-blue-500', income: '$3,500,000', expenses: '$980,000', balance: '$2,520,000' },
            { name: 'Ana', initials: 'AN', color: 'bg-pink-500', income: '$1,350,000', expenses: '$620,000', balance: '$730,000' },
          ].map((p) => (
            <div key={p.name} className="rounded-xl bg-gray-50 dark:bg-gray-700/50 p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <div className={`w-5 h-5 rounded-full ${p.color} flex items-center justify-center text-[8px] font-bold text-white`}>{p.initials}</div>
                <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">{p.name}</span>
              </div>
              <div className="grid grid-cols-3 gap-1 text-[9px]">
                <div><div className="text-gray-400">Ingresos</div><div className="font-bold text-emerald-500">{p.income}</div></div>
                <div><div className="text-gray-400">Gastos</div><div className="font-bold text-red-400">{p.expenses}</div></div>
                <div><div className="text-gray-400">Balance</div><div className="font-bold text-gray-700 dark:text-gray-200">{p.balance}</div></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MockupSplits() {
  const [splitMode, setSplitMode] = useState('equal');
  const splits = { equal: '50/50', percentage: '70/30', custom: 'Custom' };
  const values = {
    equal: ['$32,500', '$32,500'],
    percentage: ['$45,500', '$19,500'],
    custom: ['$40,000', '$25,000'],
  };

  return (
    <div className="rounded-2xl bg-white dark:bg-gray-800 shadow-lg border border-gray-100 dark:border-gray-700 p-5 w-full max-w-xs select-none">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Splits</div>
        <div className="flex gap-0.5">
          {Object.entries(splits).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSplitMode(key)}
              className={`text-[8px] font-semibold px-1.5 py-0.5 rounded-full transition-all ${
                splitMode === key ? 'bg-pink-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="text-center mb-3">
        <div className="text-[10px] text-gray-400 mb-0.5">Cena restaurante</div>
        <div className="text-xs font-bold text-gray-700 dark:text-gray-300">$65,000</div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-[9px] font-bold text-white">FR</div>
          <span className="text-[10px] font-semibold text-blue-500">{values[splitMode][0]}</span>
        </div>
        <div className="text-[10px] font-bold text-pink-400">{splits[splitMode]}</div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold text-pink-500">{values[splitMode][1]}</span>
          <div className="w-6 h-6 rounded-full bg-pink-500 flex items-center justify-center text-[9px] font-bold text-white">AN</div>
        </div>
      </div>
    </div>
  );
}

function MockupGoals() {
  const [saved, setSaved] = useState({ fr: 4000, an: 3000 });
  const target = 20000;
  const total = saved.fr + saved.an;
  const pct = Math.min(Math.round((total / target) * 100), 100);

  return (
    <div className="rounded-2xl bg-white dark:bg-gray-800 shadow-lg border border-gray-100 dark:border-gray-700 p-5 w-full max-w-xs select-none">
      <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-3">Metas en pareja</div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">🏠</span>
        <div className="flex-1">
          <div className="text-xs font-bold text-gray-800 dark:text-gray-200">Casa propia</div>
          <div className="text-[9px] text-gray-400">${(total / 1000).toFixed(0)}k / ${(target / 1000).toFixed(0)}k</div>
        </div>
        <span className="text-[10px] font-semibold text-gray-400">{pct}%</span>
      </div>
      <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex mb-2">
        <div className="h-full bg-blue-400 rounded-l-full" style={{ width: `${(saved.fr / target) * 100}%` }} />
        <div className="h-full bg-pink-400" style={{ width: `${(saved.an / target) * 100}%` }} />
      </div>
      <div className="flex gap-1">
        <button onClick={() => setSaved((s) => ({ ...s, fr: s.fr + 500 }))} className="flex-1 text-[9px] py-1 rounded bg-blue-100 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-semibold">+ Francisco</button>
        <button onClick={() => setSaved((s) => ({ ...s, an: s.an + 500 }))} className="flex-1 text-[9px] py-1 rounded bg-pink-100 dark:bg-pink-500/10 text-pink-600 dark:text-pink-400 font-semibold">+ Ana</button>
      </div>
    </div>
  );
}

export default function CoupleSection2({ onCta }) {
  const { t } = useTranslation();
  const [visibleSection, setVisibleSection] = useState(0);
  const sectionRef = useRef();

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleSection(1);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="couple" className="py-20 px-4 bg-gradient-to-b from-pink-50/50 to-white dark:from-pink-950/10 dark:to-gray-950">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-100 dark:bg-pink-500/10 text-pink-600 dark:text-pink-400 text-sm font-semibold mb-4">
            <Heart size={16} className="fill-current" />
            MODO PAREJA
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
            Finanzas en pareja,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-500">
              sin peleas
            </span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            El dinero es la causa #1 de conflictos en pareja. Kuadra2 les da las herramientas 
            para que hablen de plata sin drama.
          </p>
        </div>

        {/* Problem cards */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16 transition-all duration-700 ${visibleSection ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {PROBLEMS.map((p, i) => (
            <div key={i} className="rounded-2xl bg-white dark:bg-gray-900 border border-pink-100 dark:border-pink-900/30 p-5 text-center hover:shadow-lg transition-shadow">
              <span className="text-3xl mb-2 block">{p.icon}</span>
              <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-1">{p.title}</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">{p.desc}</p>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="text-center mb-16">
          <div className="w-16 h-1 bg-gradient-to-r from-pink-500 to-accent-500 mx-auto rounded-full mb-6" />
          <h3 className="text-2xl sm:text-3xl font-display font-bold text-gray-900 dark:text-white">
            La solución que funciona
          </h3>
        </div>

        {/* Solutions with mockups */}
        <div className="space-y-20">
          {SOLUTIONS.map((sol, i) => (
            <div
              key={i}
              className={`flex flex-col ${i % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 lg:gap-16`}
            >
              {/* Text */}
              <div className="flex-1 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-100 dark:bg-pink-500/10 text-pink-600 dark:text-pink-400 text-xs font-semibold mb-4">
                  {sol.badge}
                </div>
                <h4 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  {sol.title}
                </h4>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                  {sol.desc}
                </p>
                <ul className="space-y-2">
                  {sol.features.map((feat, fi) => (
                    <li key={fi} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <Check size={16} className="text-pink-500 shrink-0" />
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Mockup */}
              <div className={`flex-shrink-0 ${i % 2 === 0 ? 'animate-float' : 'animate-float-delayed'}`}>
                {i === 0 && <MockupSharedBalance />}
                {i === 1 && <MockupSplits />}
                {i === 2 && <MockupGoals />}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <button onClick={onCta} className="btn-primary text-lg py-4 px-10 inline-flex items-center gap-2 group">
            Empezar con mi pareja — gratis
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Ambos pueden usar la versión gratuita juntos
          </p>
        </div>
      </div>
    </section>
  );
}
